/**
 * The Orgus Project Protocol in the console (docs/architecture/project-protocol-v1.md): problems,
 * reporting status, declared actions, holds and runs, as the views need them. Pure, so tested.
 */
import { NATIVE_CHECK_LABEL, NATIVE_CHECK_LIMITS, PROBLEM_SEVERITY_LABEL, PROJECT_PROTOCOL_EXT, REPORTING_STATE_LABEL, pipelineKey } from '@orgus/protocol';
import type {
  Checkout,
  DeployTarget,
  FixDispatch,
  Id,
  NativeCheckSettings,
  Pipeline,
  Problem,
  ProblemSeverity,
  ProjectActionDecl,
  ProjectActionRole,
  ProjectActionRun,
  ProjectBusy,
  ProjectProtocolReport,
  ProjectProtocolState,
  ProjectStateFileKind,
  ReportingState,
  StateFileStatus,
  Ts,
  YieldHold,
  YieldWorkKind,
} from '@orgus/protocol';
import type { RowStatusIcon, RowTone } from '@orgus/ui';
import { formatClock, formatDate, formatDuration } from '@orgus/ui';

export type ProjectReport = ProjectProtocolState['projects'][number];
export interface StatusView {
  tone: RowTone;
  icon: RowStatusIcon;
  label: string;
}

/** state.ext['project-protocol'], when the hub sent it. */
export function ppSlice(ext: Record<string, unknown> | undefined): ProjectProtocolState | undefined {
  const s = ext?.[PROJECT_PROTOCOL_EXT] as ProjectProtocolState | undefined;
  return s && Array.isArray(s.problems) ? s : undefined;
}

// ---------------------------------------------------------------------------
// Problems
// ---------------------------------------------------------------------------

export const SEVERITY_RANK: Record<ProblemSeverity, number> = { critical: 0, warning: 1, info: 2 };

/** Most severe first, then the newest, then by key so the order never flickers. */
export function sortProblems(list: readonly Problem[]): Problem[] {
  return [...list].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || b.since - a.since || a.key.localeCompare(b.key));
}

/** A problem's one status: critical is danger, warning is attention, info is grey. */
export function problemStatus(severity: ProblemSeverity): StatusView {
  const label = PROBLEM_SEVERITY_LABEL[severity];
  if (severity === 'critical') return { tone: 'danger', icon: 'failed', label };
  if (severity === 'warning') return { tone: 'attention', icon: 'warning', label };
  return { tone: 'neutral', icon: 'idle', label };
}

/** Who raised it: the project's name, or Orgus for its own checks. */
export function problemSource(p: Pick<Problem, 'source' | 'projectId'>, projectName: (id: Id) => string | undefined): string {
  if (p.source === 'orgus') return 'Orgus';
  return (p.projectId && projectName(p.projectId)) || 'Unknown project';
}

/** Where it came from, more exactly: the project's health file or the Orgus check. */
export function problemOrigin(p: Pick<Problem, 'source' | 'check'>): string {
  if (p.source === 'project') return 'health.json';
  return p.check ? NATIVE_CHECK_LABEL[p.check] : 'Orgus check';
}

export type Target = { session: string } | { path: string };

const norm = (p: string) => p.replace(/\\/g, '/').replace(/\/+$/, '').toLowerCase();

/** The link to what a problem is about: its session, workflow, worktree, machine, item or deploy. */
export function subjectLink(
  p: Pick<Problem, 'subject' | 'projectId' | 'machineId'>,
  e: { pipelines: readonly Pick<Pipeline, 'projectId' | 'machineId' | 'adapter'>[]; checkouts: readonly Pick<Checkout, 'id' | 'path' | 'machineId' | 'projectId'>[] },
): { label: string; to: Target } | null {
  const s = p.subject;
  if (!s) return null;
  const enc = encodeURIComponent;
  switch (s.kind) {
    case 'session':
      return { label: 'Open session', to: { session: s.id } };
    case 'workflow':
      return { label: 'Open workflow', to: { path: `/agents?run=${enc(s.id)}` } };
    case 'machine':
      return { label: 'Open machine', to: { path: `/machines?machine=${enc(s.id)}` } };
    case 'worktree': {
      const c = e.checkouts.find((x) => norm(x.path) === norm(s.id) && (!p.machineId || x.machineId === p.machineId));
      if (c) return { label: 'Open worktree', to: { path: `/projects?project=${enc(c.projectId)}&checkout=${enc(c.id)}` } };
      return p.projectId ? { label: 'Open project', to: { path: `/projects?project=${enc(p.projectId)}` } } : null;
    }
    case 'pipeline-item': {
      const pl = e.pipelines.find((x) => x.projectId === p.projectId && x.machineId === p.machineId);
      return pl ? { label: 'Open item', to: { path: `/pipelines?pipeline=${enc(pipelineKey(pl))}&item=${enc(s.id)}` } } : null;
    }
    case 'deploy-target':
      return p.projectId ? { label: 'Open deploys', to: { path: `/projects?health=${enc(p.projectId)}` } } : null;
  }
}

/** Why "Dispatch a fix" cannot start for this problem, or null when it can. */
export function dispatchBlocker(p: Pick<Problem, 'projectId' | 'machineId'>, main: Pick<Checkout, 'path'> | undefined): string | null {
  if (!p.projectId) return 'This problem belongs to no project, so there is no folder to start a session in.';
  if (!p.machineId) return 'Orgus does not know which machine this problem is on.';
  if (!main) return 'Orgus has no main checkout of this project on that machine.';
  return null;
}

/** The text a fix session starts with: what, why, next and where it came from. */
export function diagnosisText(p: Pick<Problem, 'what' | 'why' | 'next' | 'source' | 'check'>, source: string, note: string): string {
  const lines = [`What: ${p.what}`, `Why: ${p.why}`, `Next: ${p.next}`, `Source: ${source}, ${problemOrigin(p)}`];
  if (note.trim()) lines.push(`Note: ${note.trim()}`);
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Needs you: problems beside the rows Orgus already shows
// ---------------------------------------------------------------------------

/**
 * Problems and Needs-you rows that say the same thing once. A session waiting on you already
 * has its own row (answer it there), so its problem is not repeated. A worktree problem replaces
 * the row about the same worktree, and a workflow problem the stuck-agent row for that run.
 */
export function dedupeWithNeeds<N extends { key: string }>(
  needs: readonly N[],
  problems: readonly Problem[],
  checkouts: readonly Pick<Checkout, 'id' | 'path' | 'machineId'>[],
): { needs: N[]; problems: Problem[] } {
  const sessionRows = new Set(needs.filter((n) => n.key.startsWith('s:')).map((n) => n.key.slice(2)));
  const kept = problems.filter((p) => !(p.subject?.kind === 'session' && sessionRows.has(p.subject.id)));
  const drop = new Set<string>();
  for (const p of kept) {
    if (p.subject?.kind === 'worktree') {
      for (const c of checkouts) if (norm(c.path) === norm(p.subject.id) && (!p.machineId || c.machineId === p.machineId)) drop.add(`w:${c.id}`);
    }
    if (p.subject?.kind === 'workflow') {
      const id = p.subject.id;
      for (const n of needs) if (n.key.startsWith('a:') && n.key.endsWith(`:${id}`)) drop.add(n.key);
    }
  }
  return { needs: needs.filter((n) => !drop.has(n.key)), problems: kept };
}

// ---------------------------------------------------------------------------
// Reports, files and declared actions
// ---------------------------------------------------------------------------

export function reportFor(s: ProjectProtocolState | undefined, projectId: Id, machineId: Id): ProjectReport | undefined {
  return s?.projects.find((r) => r.projectId === projectId && r.machineId === machineId);
}

export function reportsForProject(s: ProjectProtocolState | undefined, projectId: Id): ProjectReport[] {
  return (s?.projects ?? []).filter((r) => r.projectId === projectId);
}

export function declaredAction(r: Pick<ProjectProtocolReport, 'manifest'> | undefined, actionId: string): ProjectActionDecl | undefined {
  return r?.manifest?.actions.find((a) => a.id === actionId);
}

/** The declared actions bound to the board's Pause, Resume and Restart. */
export function roleBindings(r: Pick<ProjectProtocolReport, 'manifest'> | undefined): Partial<Record<ProjectActionRole, ProjectActionDecl>> {
  const out: Partial<Record<ProjectActionRole, ProjectActionDecl>> = {};
  for (const a of r?.manifest?.actions ?? []) if (a.role && !out[a.role]) out[a.role] = a;
  return out;
}

/** Actions for the project's own menu: the ones no board button carries. */
export function menuActions(r: Pick<ProjectProtocolReport, 'manifest'> | undefined): ProjectActionDecl[] {
  return (r?.manifest?.actions ?? []).filter((a) => !a.role);
}

/** The board's actions for a protocol pipeline: from the bound roles, as its state allows. */
export function boundBoardActions(state: Pipeline['state'], r: Pick<ProjectProtocolReport, 'manifest'> | undefined): ProjectActionRole[] {
  const b = roleBindings(r);
  const out: ProjectActionRole[] = [];
  if (b['pipeline.pause'] && state !== 'paused') out.push('pipeline.pause');
  if (b['pipeline.resume'] && state === 'paused') out.push('pipeline.resume');
  if (b['pipeline.restart']) out.push('pipeline.restart');
  return out;
}

/** The folder an action runs in: its cwd under the main checkout. */
export function actionFolder(mainPath: string | undefined, cwd: string): string {
  const rel = cwd.replace(/\\/g, '/').replace(/^\.\/?/, '').replace(/\/+$/, '');
  if (!mainPath) return rel || '.';
  const root = mainPath.replace(/[\\/]+$/, '');
  return rel ? `${root}/${rel}` : root;
}

export function mainCheckout<C extends Pick<Checkout, 'kind' | 'projectId' | 'machineId'>>(checkouts: readonly C[], projectId: Id, machineId: Id): C | undefined {
  return checkouts.find((c) => c.kind === 'main' && c.projectId === projectId && c.machineId === machineId);
}

export const FILE_NAME: Record<ProjectStateFileKind, string> = { pipeline: 'pipeline.json', health: 'health.json', deploys: 'deploys.json' };
export const FILE_ORDER: readonly ProjectStateFileKind[] = ['pipeline', 'health', 'deploys'];

export function reportingStatus(state: ReportingState): StatusView {
  const label = REPORTING_STATE_LABEL[state];
  if (state === 'reporting') return { tone: 'neutral', icon: 'done', label };
  if (state === 'not-reporting') return { tone: 'attention', icon: 'warning', label };
  return { tone: 'neutral', icon: 'idle', label };
}

/** One row per state file, in spec order; a file the agent did not list is not used. */
export function fileRows(r: Pick<ProjectProtocolReport, 'files'>): StateFileStatus[] {
  return FILE_ORDER.map((kind) => r.files.find((f) => f.kind === kind) ?? { kind, state: 'absent' as const, updatedAt: null, intervalS: 0 });
}

export const PIPELINE_SOURCE_LABEL: Record<ProjectProtocolReport['pipelineSource'], string> = {
  protocol: 'Pipeline read from pipeline.json',
  fallback: 'Pipeline read by the temporary adapter',
  none: 'No pipeline',
};

/** The pipeline file stopped reporting: since when (its last write) and why. */
export function pipelineNotReporting(r: Pick<ProjectProtocolReport, 'files' | 'pipelineSource'> | undefined): { since: Ts | null; reason: string | null } | null {
  if (!r || r.pipelineSource !== 'protocol') return null;
  const f = r.files.find((x) => x.kind === 'pipeline');
  if (!f || f.state !== 'not-reporting') return null;
  return { since: f.updatedAt, reason: f.reason ?? null };
}

/** "Not reporting since 8:41 PM" (today), "Not reporting since Oct 16" (earlier), or without a time. */
export function notReportingLabel(since: Ts | null, now: Ts): string {
  if (since == null) return 'Not reporting';
  const sameDay = new Date(since).toDateString() === new Date(now).toDateString();
  return `Not reporting since ${sameDay ? formatClock(since) : formatDate(since, now)}`;
}

/** A project's reporting in one word for its header: only said when something is off. */
export function reportingSummary(reports: readonly Pick<ProjectProtocolReport, 'files' | 'manifestError'>[]): 'not-reporting' | 'invalid' | null {
  if (reports.some((r) => r.manifestError)) return 'invalid';
  if (reports.some((r) => r.files.some((f) => f.state === 'not-reporting'))) return 'not-reporting';
  return null;
}

/**
 * A deploy that did not happen: the newest attempt was cancelled (Orgus records a refusal, such
 * as main being red, as cancelled with a message saying so) for a version other than the live
 * one, so what is live is not what was asked for. Null for a cancelled redeploy of the live
 * version, which changed nothing.
 */
export function heldDeploy(t: Pick<DeployTarget, 'behind' | 'live' | 'last'>): 'refused' | 'cancelled' | null {
  const last = t.last;
  if (last?.result !== 'cancelled') return null;
  if (!t.behind && t.live && (!last.version || last.version === t.live.version)) return null;
  return /^refused\b/i.test(last.message ?? '') ? 'refused' : 'cancelled';
}

/**
 * A deploy target's one status. "Behind" is only as sure as its source: a version someone put
 * live by hand is known; one the pipeline recorded may have been replaced outside it.
 */
export function deployStatus(t: Pick<DeployTarget, 'deploying' | 'behind' | 'live' | 'last'>): StatusView {
  if (t.deploying) return { tone: 'working', icon: 'working', label: 'Deploying' };
  if (t.last?.result === 'failed') return { tone: 'danger', icon: 'failed', label: 'Last deploy failed' };
  const held = heldDeploy(t);
  if (held) return { tone: 'attention', icon: 'warning', label: `Last deploy ${held}` };
  if (!t.live) return { tone: 'neutral', icon: 'idle', label: 'Nothing live' };
  if (t.behind) return { tone: 'neutral', icon: 'queued', label: t.live.source === 'manual' ? 'Behind main' : 'Behind main, as far as the pipeline knows' };
  return { tone: 'neutral', icon: 'done', label: 'Up to date' };
}

// ---------------------------------------------------------------------------
// Arbitration
// ---------------------------------------------------------------------------

/** "Qawl-Agents: landing 4 submissions". */
export function busyText(b: Pick<ProjectBusy, 'projectId' | 'what'>, projectName: (id: Id) => string | undefined): string {
  return `${projectName(b.projectId) ?? 'Another project'}: ${b.what}`;
}

/** "Waits for Qawl-Agents: landing 4 submissions, since 8:41 PM", or what happened to the hold. */
export function holdText(h: Pick<YieldHold, 'for' | 'since' | 'state' | 'by' | 'releasedAt'>, projectName: (id: Id) => string | undefined): string {
  const who = projectName(h.for.projectId) ?? 'another project';
  if (h.state === 'started-now') return `Started at once by ${h.by ?? 'someone'}, without waiting for ${who}`;
  if (h.state === 'timed-out') return `Started after waiting ${formatDuration((h.releasedAt ?? h.since) - h.since)} for ${who}, which was still ${h.for.kind === 'deploy' ? 'deploying' : 'landing'}`;
  if (h.state === 'released') return `Waited ${formatDuration((h.releasedAt ?? h.since) - h.since)} for ${who}: ${h.for.what}`;
  return `Waits for ${busyText(h.for, projectName)}, since ${formatClock(h.since)}`;
}

const sameBusy = (a: ProjectBusy, b: ProjectBusy) => a.projectId === b.projectId && a.machineId === b.machineId && a.kind === b.kind && a.since === b.since;

/** The work that waits for one landing or deploy, oldest first. */
export function holdsWaitingFor(s: ProjectProtocolState | undefined, busy: ProjectBusy): YieldHold[] {
  return (s?.yields ?? []).filter((h) => h.state === 'waiting' && sameBusy(h.for, busy)).sort((a, b) => a.since - b.since);
}

export interface WorkRef {
  id: Id;
  label?: string;
  projectId?: Id | null;
  machineId?: Id | null;
}

/**
 * The waiting hold of one piece of work (a queue entry, an AI job, a run): by its id when the hub
 * names it (workId), else by kind, label, project and machine.
 */
export function holdOf(s: ProjectProtocolState | undefined, kind: YieldWorkKind, work: WorkRef): YieldHold | undefined {
  const waiting = (s?.yields ?? []).filter((h) => h.kind === kind && h.state === 'waiting');
  const byId = waiting.find((h) => h.workId === work.id);
  if (byId) return byId;
  if (work.label == null) return undefined;
  return waiting.find(
    (h) => h.workId == null && h.label === work.label && (work.projectId === undefined || h.projectId === work.projectId) && (work.machineId == null || h.machineId === work.machineId),
  );
}

export function holdById(s: ProjectProtocolState | undefined, id: Id | undefined): YieldHold | undefined {
  return id ? s?.yields.find((h) => h.id === id) : undefined;
}

/** Every landing or deploy in progress on a machine, from every project there. */
export function busyOn(s: ProjectProtocolState | undefined, machineId: Id): ProjectBusy[] {
  return (s?.projects ?? []).filter((r) => r.machineId === machineId).flatMap((r) => r.busy);
}

// ---------------------------------------------------------------------------
// Runs
// ---------------------------------------------------------------------------

/**
 * A run's one status. A heavy run held for a landing reads as waiting, not running: its hold is
 * waiting, or (without the hold at hand) it has a yieldId and has not started.
 */
export function runStatus(run: Pick<ProjectActionRun, 'state' | 'exitCode' | 'yieldId' | 'startedAt'>, hold?: Pick<YieldHold, 'state'>): StatusView {
  switch (run.state) {
    case 'awaiting-confirmation':
      return { tone: 'attention', icon: 'needs-you', label: 'Needs you' };
    case 'running':
      return isRunWaiting(run, hold) ? { tone: 'neutral', icon: 'queued', label: 'Waiting' } : { tone: 'working', icon: 'working', label: 'Running' };
    case 'succeeded':
      return { tone: 'success', icon: 'done', label: 'Succeeded' };
    case 'failed':
      return { tone: 'danger', icon: 'failed', label: run.exitCode != null ? `Failed, exit ${run.exitCode}` : 'Failed' };
    case 'cancelled':
      return { tone: 'neutral', icon: 'stopped', label: 'Cancelled' };
    case 'refused':
      return { tone: 'danger', icon: 'failed', label: 'Refused' };
  }
}

export function isRunWaiting(run: Pick<ProjectActionRun, 'state' | 'yieldId' | 'startedAt'>, hold?: Pick<YieldHold, 'state'>): boolean {
  if (run.state !== 'running') return false;
  if (hold) return hold.state === 'waiting';
  return !!run.yieldId && run.startedAt == null;
}

/**
 * The run a refused Pause, Resume or Restart turned into: on a protocol pipeline the hub answers
 * POST /api/actions with 403 (code 'project-protocol') and names the run waiting for a person,
 * "(run <id>)". Null for any other error.
 */
export function protocolRunId(e: unknown): string | null {
  const x = e as { code?: string; why?: string; what?: string; message?: string; ui?: { code?: string; why?: string; what?: string } } | null;
  if (!x || typeof x !== 'object') return null;
  const text = [x.why, x.what, x.message, x.ui?.why, x.ui?.what].filter((t): t is string => typeof t === 'string').join(' ');
  const m = /\(run ([A-Za-z0-9_.:-]+)\)/.exec(text);
  return m ? m[1]! : null;
}

export const isRunLive =(run: Pick<ProjectActionRun, 'state'>) => run.state === 'running' || run.state === 'awaiting-confirmation';

/** The newest runs of one project, newest first. */
export function projectRuns(s: ProjectProtocolState | undefined, projectId: Id, limit = 5): ProjectActionRun[] {
  return (s?.runs ?? [])
    .filter((r) => r.projectId === projectId)
    // A run waiting for a landing has not started: it is the newest.
    .sort((a, b) => (b.startedAt ?? Number.MAX_SAFE_INTEGER) - (a.startedAt ?? Number.MAX_SAFE_INTEGER))
    .slice(0, limit);
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export type CheckKey = keyof typeof NATIVE_CHECK_LIMITS;
export type SettingsErrors = Partial<Record<CheckKey, string>>;

const PCT: readonly CheckKey[] = ['cpuWarnPct', 'cpuCriticalPct'];

/** Whole numbers in the hub's ranges (NATIVE_CHECK_LIMITS), and no critical before its warning. */
export function validateCheckSettings(s: NativeCheckSettings): SettingsErrors {
  const e: SettingsErrors = {};
  for (const k of Object.keys(NATIVE_CHECK_LIMITS) as CheckKey[]) {
    const [min, max] = NATIVE_CHECK_LIMITS[k];
    const v = s[k];
    if (!Number.isInteger(v) || v < min || v > max) e[k] = `Enter a whole number of ${PCT.includes(k) ? 'percent' : k === 'autoFixMaxAttempts' ? 'tries' : 'minutes'} from ${min} to ${max}.`;
  }
  const after = (warn: CheckKey, crit: CheckKey, unit: string) => {
    if (!e[warn] && !e[crit] && s[crit] < s[warn]) e[crit] = `Make it at least the warning (${s[warn]}${unit}).`;
  };
  after('sessionWaitingWarnMin', 'sessionWaitingCriticalMin', ' min');
  after('workflowStalledWarnMin', 'workflowStalledCriticalMin', ' min');
  after('cpuWarnPct', 'cpuCriticalPct', '%');
  return e;
}

/**
 * The newest fix for a problem and how it reads (ADR 0064): Orgus fixing it on its own, a person's
 * fix session, or a fix that did not work. Null when nobody started one.
 */
export function fixView(problemKey: string, dispatches: readonly FixDispatch[] | undefined): (StatusView & { fix: FixDispatch; auto: boolean }) | null {
  const fix = [...(dispatches ?? [])].filter((d) => d.problemKey === problemKey).sort((a, b) => b.at - a.at)[0];
  if (!fix) return null;
  const auto = !!fix.auto;
  if (auto && fix.gaveUp) return { fix, auto, tone: 'danger', icon: 'failed', label: 'Orgus could not fix this' };
  if (fix.state === 'failed') return { fix, auto, tone: 'danger', icon: 'failed', label: auto ? 'Orgus could not start a fix' : 'Fix session did not start' };
  if (auto) return { fix, auto, tone: 'working', icon: 'working', label: (fix.attempt ?? 1) > 1 ? `Orgus is fixing this, try ${fix.attempt}` : 'Orgus is fixing this' };
  if (fix.state === 'starting') return { fix, auto, tone: 'working', icon: 'working', label: 'Fix session starting' };
  return { fix, auto, tone: 'neutral', icon: 'done', label: 'Fix session started' };
}

/** Orgus is fixing this problem on its own right now, so it does not need a person (ADR 0064). */
export const orgusIsFixing = (problemKey: string, dispatches: readonly FixDispatch[] | undefined): boolean => {
  const v = fixView(problemKey, dispatches);
  return !!v && v.auto && v.tone === 'working';
};
